import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { upperFirst, toLower, partition, matchesProperty, pick } from 'lodash';

import { defaqtoSourceData } from '@starlingbank/shared';

import { trackGtm } from '../../utils';

import TextContent from '../textContent/TextContent';
import Tooltip from '../tooltip/Tooltip';

import tick from '../../../content/media-library/icons/yes.svg';
import cross from '../../../content/media-library/icons/no.svg';
import starling from '../../../content/media-library/logos/tables/Starling.png';

const getQuestionById = (id, questions) => questions.find((q) => q.id === id);
const getAnswer = (questionId, answers) => answers.find((a) => a.qid === questionId);
const sentenceCase = (s) => upperFirst(toLower(s));
const spaceAfterComma = (s) => s.replace(/(\w),(\w)/g, '$1, $2');
const questionTextEnhancements = (s) => {
  if (s.includes('Number of Mobile App Features')) {
    return `${sentenceCase(s)} for protecting your account`;
  }

  const sentCased = sentenceCase(s);
  return sentCased
    .replace(/^Atm(.*)/, 'ATM$1')
    .replace('Aer ', ' AER ')
    .replace(' eu ', ' EU ');
};

const DataRow = ({ question, starlingAnswer, otherCurrentAccounts, selectedOptions, questionId }) => {
  let col1 = otherCurrentAccounts.find((item) => item.id === selectedOptions.provider1);
  let col2 = otherCurrentAccounts.find((item) => item.id === selectedOptions.provider2);
  let col1Answers, col2Answers;
  if (col1 && col1.answers) col1Answers = getAnswer(questionId, col1.answers);
  if (col2 && col2.answers) col2Answers = getAnswer(questionId, col2.answers);

  return (
    <Row key={questionId} className="sme-business-toolkit__row">
      <Header
        heading={questionTextEnhancements(question.text)}
        bio={question.help}
        className="sme-business-toolkit__sub-heading"
      />
      <DataCell
        icon={tick}
        data={starlingAnswer}
        className="sme-business-toolkit__col sme-business-toolkit__col--grey"
      />
      <DataCell icon={tick} data={col1Answers} className="sme-business-toolkit__col" />
      <DataCell
        icon={tick}
        data={col2Answers}
        className="sme-business-toolkit__col sme-business-toolkit__col--hide-mobile"
      />
    </Row>
  );
};

const Row = ({ className, children }) => {
  return <tr className={className}>{children}</tr>;
};

const GOOD_ALT_TEXT = 'meets or exceeds the benefit';
const BAD_ALT_TEXT = 'does not meet the benefit';

const DataCell = ({ data, className }) => {
  return (
    <td className={className}>
      {data &&
        (data.altText && data.altText.toLowerCase().indexOf(GOOD_ALT_TEXT) > -1 ? <img alt="Yes" src={tick} /> : null)}
      {data &&
        (data.altText && data.altText.toLowerCase().indexOf(BAD_ALT_TEXT) > -1 ? <img alt="No" src={cross} /> : null)}
      {data && data.displayText ? <p>{spaceAfterComma(data.displayText)}</p> : ''}
    </td>
  );
};

const Header = ({ heading, bio, className }) => {
  return (
    <th scope="row" className={className}>
      <p>{heading}</p>
      {bio && <Tooltip heading={heading} bio={bio} />}
    </th>
  );
};

const productFromId = (products, productId) => products.find((product) => product.id === productId);

const Provider = ({ label, productId }) => {
  let acc = productFromId(defaqtoSourceData.products, productId);
  if (!acc) acc = { name: 'Please select a provider below' };
  const [bank, accountType] = acc.name.split(' - ');

  return (
    <li className="sme-business-toolkit__list-item">
      <p>
        <strong>{label}</strong>
      </p>
      <p>{bank}</p>
      <p>
        <em>{accountType}</em>
      </p>
    </li>
  );
};

const DefaqtoCustomTable = ({ categories = [] }) => {
  const { lastUpdated, groups, questions, products } = defaqtoSourceData;
  const [[starlingProduct], otherCurrentAccounts] = partition(products, matchesProperty('id', '360131'));
  const [selectedOptions, setSelectedOptions] = useState({
    provider1: 'Please select a provider below',
    provider2: 'Please select a provider below'
  });

  // ?provider1=384729&provider2=439152
  useEffect(() => {
    if (!window.location.search) return;
    const paramString = window.location.search.slice(1);
    const paramObject = Object.fromEntries(new URLSearchParams(paramString));
    setSelectedOptions((selectedOptions) => ({
      ...selectedOptions,
      ...pick(paramObject, ['provider1', 'provider2'])
    }));
  }, []);

  const updateTable = (column, event) => {
    const selectedProduct = event.target.value;
    setSelectedOptions({
      ...selectedOptions,
      [`provider${column}`]: !selectedProduct ? 'Please select a provider below' : selectedProduct
    });

    const matchingProduct = productFromId(products, selectedProduct);

    // Track usage of the selects on the comparison table
    trackGtm({
      event: 'defaqto_select_bank_account',
      column,
      selected_product: matchingProduct?.name || selectedProduct
    });
  };

  return (
    <Fragment>
      <ol className="sme-business-toolkit__list">
        <Provider label="Provider 1:" productId={selectedOptions.provider1} />
        <Provider label="Provider 2:" productId={selectedOptions.provider2} />
      </ol>
      <table className="sme-business-toolkit">
        <caption className="screen-reader-text">Compare current accounts. Data correct as of {lastUpdated}.</caption>
        <thead className="sme-business-toolkit__header">
          <Row className="sme-business-toolkit__row sme-business-toolkit__row--header sme-business-toolkit__row--sticky">
            {/* eslint-disable-next-line */}
            <th></th>
            <th className="sme-business-toolkit__col sme-business-toolkit__col--20 sme-business-toolkit__col--grey">
              <div className="sme-business-toolkit__header-box">
                <img src={starling} alt="Starling Bank logo" />
                <p>Starling Bank</p>
              </div>
            </th>
            <th className="sme-business-toolkit__col sme-business-toolkit__col--20 sme-business-toolkit__col--border">
              <label htmlFor="provider-1" className="screen-reader-text">
                Please select a provider to compare
              </label>
              {/* eslint-disable-next-line */}
              <select
                id="provider-1"
                className="sme-business-toolkit__selector"
                value={selectedOptions.provider1}
                onChange={(e) => updateTable(1, e)}
              >
                <option value="">Provider 1...</option>
                {otherCurrentAccounts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </th>
            <th className="sme-business-toolkit__col sme-business-toolkit__col--20 sme-business-toolkit__col--hide-mobile">
              <label htmlFor="provider-2" className="screen-reader-text">
                Please select a provider to compare
              </label>
              {/* eslint-disable-next-line */}
              <select
                id="provider-2"
                className="sme-business-toolkit__selector"
                value={selectedOptions.provider2}
                onChange={(e) => updateTable(2, e)}
              >
                <option value="">Provider 2...</option>
                {otherCurrentAccounts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </th>
          </Row>
        </thead>
        <tbody>
          {groups
            .filter((group) => {
              if (categories.length) {
                return categories.includes(group.name.toLowerCase());
              }
              return group;
            })
            .map((group) => (
              <Fragment key={group.id}>
                <Row className="sme-business-toolkit__row sme-business-toolkit__row--grey">
                  <Header heading={group.name} className="sme-business-toolkit__heading" />
                  <DataCell className="sme-business-toolkit__col" />
                  <DataCell className="sme-business-toolkit__col sme-business-toolkit__col--grey" />
                  <DataCell className="sme-business-toolkit__col sme-business-toolkit__col--hide-mobile" />
                </Row>
                {group.questions.map((questionId) => {
                  const question = getQuestionById(questionId, questions);
                  const starlingAnswer = getAnswer(questionId, starlingProduct.answers);
                  return (
                    <DataRow
                      key={questionId}
                      question={question}
                      questionId={questionId}
                      starlingAnswer={starlingAnswer}
                      otherCurrentAccounts={otherCurrentAccounts}
                      selectedOptions={selectedOptions}
                    />
                  );
                })}
              </Fragment>
            ))}
        </tbody>
      </table>
      <TextContent text={`Data correct as of ${lastUpdated}.`} customPadding="no-top" />
    </Fragment>
  );
};

DefaqtoCustomTable.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string)
};

export default DefaqtoCustomTable;
